import { BaseState, LoadingStatus } from '@/types';

// GraphQL error class types for content feature
export enum ContentGraphQLErrorClass {
  CONTENT_CREATION_FAILED = "App\\Exceptions\\Content\\ContentCreationFailedException",
  CONTENT_NOT_FOUND = "App\\Exceptions\\Content\\ContentNotFoundException",
  CONTENT_PROCESSING_FAILED = "App\\Exceptions\\Content\\ContentProcessingFailedException",
  UPLOAD_FAILED = "App\\Exceptions\\Content\\UploadFailedException",
  FILE_SIZE_EXCEEDED = "App\\Exceptions\\Content\\FileSizeExceededException",
  INVALID_FILE_TYPE = "App\\Exceptions\\Content\\InvalidFileTypeException"
}

// Content processing status enum
export enum ContentProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// Content types
export enum ContentType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document'
}

// Interface for content metadata
export interface ContentMetadata {
  width?: number;
  height?: number;
  duration?: number;
  size: number;
  mimeType: string;
}

// Interface for content item
export interface Content {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  processingStatus: ContentProcessingStatus;
  downloadUrl: string;
  createdAt: string;
  updatedAt: string;
  type: ContentType;
  metadata?: ContentMetadata;
  userId: string;
}

// Interface for media conversions in upload response  
export interface MediaConversions {
  original?: string;
  small?: string;
  medium?: string;
}

// Interface for user in upload response
export interface UploadUser {
  id: string;
  name: string;
  nickname?: string;
  profile_photo_urls?: {
    thumbnail?: string;
    small?: string;
    medium?: string;
  };
}

// Interface for content in upload response
export interface UploadContentData {
  id: string;
  file_name: string;
  size: number;
  mime_type: string;
  processing_status: string;
  download_url: string;
  media_conversions?: MediaConversions;
  applause_count: number;
  comment_count: number;
  is_applauded: boolean;
  is_reported: boolean;
  user: UploadUser;
  created_at: string;
  updated_at: string;
}

// Interface for upload response from API
export interface UploadContentResponse {
  success: boolean;
  message: string;
  content: UploadContentData;
}

// Interface for individual upload item
export interface UploadItem {
  id: string;
  file: File;
  status: LoadingStatus;
  progress: number;
  content: Content | null;
  error: string | null;
  startedAt: Date;
}

// Interface for upload content state in Redux
export interface ContentState extends BaseState<Content[]> {
  currentUpload: {
    status: LoadingStatus;
    progress: number;
    content: Content | null;
    error: string | null;
  };
  uploads: UploadItem[];
}

// Interface for latest content user data
export interface LatestContentUser {
  id: string;
  name: string;
  nickname?: string;
  bio?: string;
  profile_photo_urls?: {
    thumbnail?: string;
    small?: string;
    medium?: string;
  };
  followers_count: number;
  following_count: number;
  applauses_count: number;
  is_followed: boolean;
  is_follower: boolean;
  is_me: boolean;
}

// Interface for latest content item
export interface LatestContentItem {
  id: string;
  file_name: string;
  size: number;
  mime_type: string;
  processing_status: string;
  download_url: string;
  media_conversions?: MediaConversions;
  applause_count: number;
  comment_count: number;
  is_applauded: boolean;
  is_reported: boolean;
  user: LatestContentUser;
  created_at: string;
  updated_at: string;
}

// Interface for pagination info
export interface PaginatorInfo {
  total: number;
  count: number;
  currentPage: number;
  lastPage: number;
  firstItem: number;
  lastItem: number;
  perPage: number;
  hasMorePages: boolean;
}

// Interface for latest content response
export interface LatestContentResponse {
  latestContent: {
    data: LatestContentItem[];
    paginatorInfo: PaginatorInfo;
  };
}

// Interface for user content response
export interface UserContentResponse {
  userContent: {
    data: LatestContentItem[];
    paginatorInfo: PaginatorInfo;
  };
}

// Interface for comment user
export interface CommentUser {
  id: string;
  name: string;
  nickname: string;
  profile_photo_urls?: {
    thumbnail?: string;
    small?: string;
    medium?: string;
  };
}

// Interface for comment parent
export interface CommentParent {
  id: string;
  text: string;
  user: {
    name: string;
    nickname: string;
  };
}

// Interface for comment reply
export interface CommentReply {
  id: string;
  text: string;
  user: {
    id: string;
    name: string;
    nickname: string;
  };
  created_at: string;
}

// Interface for comment replies response
export interface CommentReplies {
  data: CommentReply[];
  paginatorInfo: {
    total: number;
    hasMorePages: boolean;
  };
}

// Interface for individual comment
export interface Comment {
  id: string;
  text: string;
  user: CommentUser;
  parent?: CommentParent;
  replies?: CommentReplies;
  created_at: string;
  updated_at: string;
}

// Interface for comments response
export interface ContentCommentsResponse {
  contentComments: {
    data: Comment[];
    paginatorInfo: PaginatorInfo;
  };
} 